import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, setAccessTokenProvider, setSiteIdProvider, type LearnerProfileFull } from './api';
import { useCurrentUser } from './auth/LoginGate';
import { PilotForm } from './components/PilotForm';
import { MatrixViewer } from './components/MatrixViewer';
import { InterviewPanel } from './components/InterviewPanel';
import { ReportViewer } from './components/ReportViewer';
import { MetricsView } from './components/MetricsView';
import { OACatalogView } from './components/OACatalogView';
import { LearnerView } from './components/LearnerView';
import { DashboardView } from './components/DashboardView';
import { AdminPromptsView } from './components/AdminPromptsView';
import { AuditView } from './components/AuditView';
import { ClassroomCreator } from './components/ClassroomCreator';
import { ClassroomsList } from './components/ClassroomsList';
import { ClassroomLive } from './components/ClassroomLive';
import { HistorySidebar, type HistoryFilters } from './components/HistorySidebar';
import { FullscreenLoader } from './components/FullscreenLoader';
import { AppShell } from './components/AppShell';
import {
  MODALITY_LABEL,
  type ChatTurn,
  type Dictamen,
  type Dim,
  type HelpType,
  type MarzanoMatrix,
  type Modality,
  type PilotInput,
  type SessionListItem,
  type SessionRecord,
} from './types';
import {
  buildGrantedHelpEntry,
  buildHelpChatUpdate,
  buildTurnChatUpdate,
} from './hooks/interviewReducers';

type Screen =
  | 'form'
  | 'matrix'
  | 'interview'
  | 'report'
  | 'metrics'
  | 'catalog'
  | 'learner'
  | 'dashboard'
  | 'admin-prompts'
  | 'admin-audit'
  | 'classroom-nueva'
  | 'classrooms-list'
  | 'classroom-live';

interface GrantedHelp {
  help_type: HelpType;
  penalty_pct: number;
  at: string; // ISO
  dim: Dim | null;
}

interface CurrentPilot {
  sessionId: string;
  siteId: string;
  input: PilotInput;
  matrix: MarzanoMatrix;
  session: SessionRecord | null;
  chatHistory: ChatTurn[];
  currentDim: Dim | null;
  evidenceCounts: Record<Dim, number>;
  dictamen: Dictamen | null;
  grantedHelps: GrantedHelp[];
}

// Placeholder pre-identity: el useEffect de historySiteId lo pisa apenas
// se resuelve /v1/me con currentSiteId real (hex 24, mongo ObjectId).
// Los efectos que dependen de historySiteId son no-op mientras esté vacío.
const DEFAULT_SITE_ID = '';

export function App() {
  const { identity, getAccessToken } = useCurrentUser();
  const isLearner = identity?.activeRole === 'student';

  useEffect(() => {
    setAccessTokenProvider(getAccessToken);
    return () => setAccessTokenProvider(null);
  }, [getAccessToken]);

  useEffect(() => {
    setSiteIdProvider(() => identity?.currentSiteId ?? null);
    return () => setSiteIdProvider(null);
  }, [identity]);

  // El identity resuelve async; hasta entonces historySiteId es ''. Cuando
  // llega, sincronizamos para que las queries del historial arranquen con
  // la sede activa (no la del último pilot recargado).
  useEffect(() => {
    if (identity?.currentSiteId) {
      setHistorySiteId((prev) => prev || identity.currentSiteId);
    }
  }, [identity]);

  // Pantalla de inicio: el formulario de piloto individual ya no es la portada.
  // Docentes/admin aterrizan en sus aulas; el alumno en su dashboard.
  const homeScreen: Screen = isLearner ? 'dashboard' : 'classrooms-list';
  const [screen, setScreen] = useState<Screen>(homeScreen);
  // Contexto para la vista live de aula (se setea al elegir un aula desde el
  // listado). Null cuando el screen no es 'classroom-live'.
  const [activeClassroomId, setActiveClassroomId] = useState<string | null>(null);
  useEffect(() => {
    if (isLearner && screen === 'form') setScreen('dashboard');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLearner]);
  const [pilot, setPilot] = useState<CurrentPilot | null>(null);
  const [creating, setCreating] = useState(false);
  const [starting, setStarting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [presetLearner, setPresetLearner] = useState<LearnerProfileFull | null>(null);
  const [sessions, setSessions] = useState<SessionListItem[]>([]);
  const [sessionsTotal, setSessionsTotal] = useState(0);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historySiteId, setHistorySiteId] = useState(DEFAULT_SITE_ID);
  const [historyFilters, setHistoryFilters] = useState<HistoryFilters>({});
  const [historyPage, setHistoryPage] = useState(0);
  const HISTORY_PAGE_SIZE = 20;

  const reloadHistory = useCallback(
    async (siteId: string, filters: HistoryFilters, page: number) => {
      setLoadingHistory(true);
      try {
        const resp = await api.listPilots(siteId, {
          limit: HISTORY_PAGE_SIZE,
          offset: page * HISTORY_PAGE_SIZE,
          ...filters,
        });
        setSessions(resp.sessions);
        setSessionsTotal(resp.total);
      } catch {
        // sin historial el flujo sigue funcionando
      } finally {
        setLoadingHistory(false);
      }
    },
    [],
  );

  useEffect(() => {
    // Learners no llaman a /v1/pilots (403).
    if (isLearner) return;
    // Sin site resuelto (pre-identity) no fetcheamos — evita 400 SITE_REQUIRED.
    if (!historySiteId) return;
    void reloadHistory(historySiteId, historyFilters, historyPage);
  }, [reloadHistory, historySiteId, historyFilters, historyPage, isLearner]);

  // Ref lockea sincrónicamente contra double-click; setState no bastaría.
  const createLockRef = useRef(false);
  const startLockRef = useRef(false);
  // CT-H5: sin refs sincrónicos, submitting/grantingType son state async y 2
  // eventos en el mismo tick pasaban ambos → doble ANSWER_RECEIVED/HELP_GRANTED.
  const submitTextLockRef = useRef(false);
  const submitAudioLockRef = useRef(false);
  const grantHelpLockRef = useRef(false);
  const closeEarlyLockRef = useRef(false);

  const handleCreatePilot = async (input: PilotInput) => {
    if (createLockRef.current) return;
    createLockRef.current = true;
    setCreating(true);
    setFormError(null);
    try {
      const resp = await api.createPilot(input);
      setPilot({
        sessionId: resp.session_id,
        siteId: input.site_id,
        input,
        matrix: resp.matrix,
        session: null,
        chatHistory: [],
        currentDim: 'recuperacion',
        evidenceCounts: {
          recuperacion: 0,
          comprension: 0,
          analisis: 0,
          transferencia: 0,
        },
        dictamen: null,
        grantedHelps: [],
      });
      setHistorySiteId(input.site_id);
      setScreen('matrix');
    } catch (e: unknown) {
      setFormError(formatError(e));
    } finally {
      createLockRef.current = false;
      setCreating(false);
    }
  };

  const handleStartInterview = async () => {
    if (!pilot || startLockRef.current) return;
    startLockRef.current = true;
    setStarting(true);
    try {
      const resp = await api.startInterview(pilot.sessionId, pilot.siteId);
      const state = await api.getPilot(pilot.sessionId, pilot.siteId);
      setPilot({
        ...pilot,
        session: state.session,
        chatHistory: state.recent_turns,
        currentDim: (resp.current_dim as Dim) ?? 'recuperacion',
        evidenceCounts: state.session.evidence_counts,
      });
      setScreen('interview');
    } catch (e: unknown) {
      alert(formatError(e));
    } finally {
      startLockRef.current = false;
      setStarting(false);
    }
  };

  const applyTurnResponse = (resp: Awaited<ReturnType<typeof api.submitTurnText>>, learnerTurn: ChatTurn) => {
    setPilot((p) =>
      p
        ? {
            ...p,
            chatHistory: buildTurnChatUpdate(p.chatHistory, resp, learnerTurn),
            currentDim: resp.current_dim,
            evidenceCounts: resp.evidence_counts,
            dictamen: resp.dictamen ?? p.dictamen,
          }
        : p,
    );
    if (resp.closed && resp.dictamen) {
      setScreen('report');
      void reloadHistory(historySiteId, historyFilters, historyPage);
    }
  };

  const handleSubmitText = async (
    text: string,
    seconds: number,
    metadata?: { paste_attempts: number; answer_time_ms: number },
  ) => {
    if (!pilot) throw new Error('no pilot');
    if (submitTextLockRef.current) return undefined;
    submitTextLockRef.current = true;
    setSubmitting(true);
    try {
      const resp = await api.submitTurnText(
        pilot.sessionId,
        pilot.siteId,
        text,
        seconds,
        metadata,
      );
      applyTurnResponse(resp, {
        role: 'learner',
        text,
        observed_at: new Date().toISOString(),
        modality: 'written',
        ...(resp.ai_suspicion && { aiSuspicion: resp.ai_suspicion }),
      });
      return resp;
    } finally {
      setSubmitting(false);
      submitTextLockRef.current = false;
    }
  };

  const handleSubmitAudio = async (blob: Blob, duration: number) => {
    if (!pilot) throw new Error('no pilot');
    if (submitAudioLockRef.current) return undefined;
    submitAudioLockRef.current = true;
    setSubmitting(true);
    try {
      const resp = await api.submitTurnAudio(pilot.sessionId, pilot.siteId, blob, duration);
      applyTurnResponse(resp, {
        role: 'learner',
        text: resp.transcript ?? '(audio sin transcripción)',
        observed_at: new Date().toISOString(),
        modality: 'oral',
      });
      return resp;
    } finally {
      setSubmitting(false);
      submitAudioLockRef.current = false;
    }
  };

  const handleGrantHelp = async (helpType: HelpType) => {
    if (!pilot) return;
    if (grantHelpLockRef.current) return;
    grantHelpLockRef.current = true;
    try {
      const resp = await api.registerHelp(pilot.sessionId, pilot.siteId, helpType);
      setPilot((p) =>
        p
          ? {
              ...p,
              chatHistory: buildHelpChatUpdate(p.chatHistory, resp, helpType),
              grantedHelps: [
                ...p.grantedHelps,
                buildGrantedHelpEntry(resp, helpType, p.currentDim),
              ],
            }
          : p,
      );
    } catch (e: unknown) {
      // CT-H5: sin este catch, fallo del backend dejaba al docente sin señal.
      alert(formatError(e));
    } finally {
      grantHelpLockRef.current = false;
    }
  };

  const handleCloseEarly = async (
    reason: 'teacher_early_close' | 'time_exhausted' = 'teacher_early_close',
  ) => {
    if (!pilot) return;
    if (closeEarlyLockRef.current) return;
    // `time_exhausted` viene del cronómetro; no hay nada que confirmar.
    if (reason === 'teacher_early_close') {
      const confirmed = window.confirm(
        'Cerrar la entrevista ahora y generar el dictamen final con la evidencia acumulada?',
      );
      if (!confirmed) return;
    }
    closeEarlyLockRef.current = true;
    setSubmitting(true);
    try {
      const { dictamen } = await api.closePilot(pilot.sessionId, pilot.siteId, reason);
      setPilot({ ...pilot, dictamen });
      setScreen('report');
      void reloadHistory(historySiteId, historyFilters, historyPage);
    } catch (e: unknown) {
      alert(formatError(e));
    } finally {
      closeEarlyLockRef.current = false;
      setSubmitting(false);
    }
  };

  const handleOpenFromHistory = async (sessionId: string) => {
    try {
      const state = await api.getPilot(sessionId, historySiteId);
      const oa = state.session.oa_snapshot as {
        oa_codigo?: string;
        oa_descripcion?: string;
        asignatura?: string;
        nivel_academico?: string;
        tipo?: PilotInput['oa_tipo'];
        matrix?: MarzanoMatrix;
        oas?: Array<{ oa_codigo: string; oa_descripcion: string; tipo?: PilotInput['oa_tipo'] }>;
      };
      const hito = state.session.milestone_snapshot as {
        hito_nombre?: string;
        hito_descripcion?: string;
      };
      if (!oa.matrix) {
        alert('Sesión sin matriz persistida; no se puede reabrir.');
        return;
      }
      const reconstructedInput: PilotInput = {
        site_id: String(state.session.site_id),
        student_name: state.session.mongo_learner_id, // fallback
        asignatura: oa.asignatura ?? '',
        nivel_academico: oa.nivel_academico ?? '',
        oa_codigo: oa.oa_codigo ?? '',
        oa_descripcion: oa.oa_descripcion ?? '',
        hito_nombre: hito.hito_nombre ?? '',
        hito_descripcion: hito.hito_descripcion ?? '',
        modality: state.session.modality,
        oa_tipo: oa.tipo ?? 'conceptual',
        ...(oa.oas && oa.oas.length > 0 && { oas: oa.oas }),
      };
      setPilot({
        sessionId: state.session.id,
        siteId: String(state.session.site_id),
        input: reconstructedInput,
        matrix: oa.matrix,
        session: state.session,
        chatHistory: state.recent_turns,
        currentDim: state.session.current_dim,
        evidenceCounts: state.session.evidence_counts,
        dictamen: state.dictamen,
        // TODO: rehidratar grantedHelps desde HELP_GRANTED (endpoint de read pendiente).
        grantedHelps: [],
      });
      setScreen(state.dictamen !== null ? 'report' : 'interview');
    } catch (e: unknown) {
      alert(formatError(e));
    }
  };

  const handleBackToList = () => {
    setPilot(null);
    setScreen(homeScreen);
    void reloadHistory(historySiteId, historyFilters, historyPage);
  };

  // Entrevista activa: sin chrome para no distraer al docente.
  const hideChrome = screen === 'interview';

  return (
    <AppShell
      activeScreen={screen}
      onNavigate={(id) => setScreen(id)}
      hideChrome={hideChrome}
    >
      {creating && (
        <FullscreenLoader
          title="Iniciando evaluación…"
          subtitle="Preparando la sesión del alumno con la matriz pedagógica."
        />
      )}
      {starting && (
        <FullscreenLoader
          title="Cargando la primera pregunta…"
          subtitle="El profesor virtual está armando la primera pregunta para el estudiante."
        />
      )}
      <div className="w-full">
        {screen === 'form' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <PilotForm
                defaultSiteId={historySiteId}
                presetLearner={presetLearner}
                onSubmit={async (input) => {
                  setPresetLearner(null);
                  await handleCreatePilot(input);
                }}
                isLoading={creating}
                error={formError}
              />
            </div>
            {/* Sidebar solo para teachers/admin; learners lo ven en Dashboard. */}
            {!isLearner && (
              <div className="lg:col-span-1">
                <HistorySidebar
                  sessions={sessions}
                  total={sessionsTotal}
                  page={historyPage}
                  pageSize={HISTORY_PAGE_SIZE}
                  filters={historyFilters}
                  onOpen={handleOpenFromHistory}
                  onFiltersChange={(f) => {
                    setHistoryFilters(f);
                    setHistoryPage(0);
                  }}
                  onPageChange={(p) => setHistoryPage(p)}
                  isLoading={loadingHistory}
                />
              </div>
            )}
          </div>
        )}

        {screen === 'matrix' && pilot && (
          <MatrixViewer
            matrix={pilot.matrix}
            oaCodigo={pilot.input.oa_codigo}
            oaDescripcion={pilot.input.oa_descripcion}
            {...(pilot.input.oas && { oas: pilot.input.oas })}
            asignatura={pilot.input.asignatura}
            nivel={pilot.input.nivel_academico}
            hitoNombre={pilot.input.hito_nombre}
            studentName={pilot.input.student_name}
            modality={MODALITY_LABEL[pilot.input.modality]}
            onBack={() => setScreen(homeScreen)}
            onStart={handleStartInterview}
            isStarting={starting}
          />
        )}

        {screen === 'interview' && pilot && pilot.session && (
          <InterviewPanel
            sessionId={pilot.sessionId}
            siteId={pilot.siteId}
            initialBudgetSec={pilot.session.initial_budget_sec}
            oralSecondsUsedInitial={pilot.session.oral_seconds_used}
            textSecondsUsedInitial={pilot.session.text_seconds_used}
            modality={pilot.input.modality}
            studentName={pilot.input.student_name}
            oaCodigo={pilot.input.oa_codigo}
            hitoNombre={pilot.input.hito_nombre}
            chatHistory={pilot.chatHistory}
            currentDim={pilot.currentDim}
            evidenceCounts={pilot.evidenceCounts}
            isAwaitingFirst={pilot.chatHistory.length === 0}
            isBusy={submitting}
            onSubmitText={handleSubmitText}
            onSubmitAudio={handleSubmitAudio}
            onGrantHelp={handleGrantHelp}
            grantedHelps={pilot.grantedHelps}
            onCloseEarly={() => handleCloseEarly('teacher_early_close')}
            onTimeExhausted={() => handleCloseEarly('time_exhausted')}
          />
        )}

        {screen === 'metrics' && (
          <MetricsView siteId={historySiteId} onClose={() => setScreen(homeScreen)} />
        )}

        {screen === 'dashboard' && (
          <DashboardView
            onClose={() => setScreen(homeScreen)}
            onOpenPilot={(sessionId) => void handleOpenFromHistory(sessionId)}
          />
        )}

        {screen === 'admin-prompts' && (
          <AdminPromptsView onClose={() => setScreen(homeScreen)} />
        )}

        {screen === 'classroom-nueva' && (
          <ClassroomCreator
            onClose={() => setScreen('classrooms-list')}
            onOpenLive={(id) => {
              setActiveClassroomId(id);
              setScreen('classroom-live');
            }}
          />
        )}

        {screen === 'classrooms-list' && (
          <ClassroomsList
            onNew={() => setScreen('classroom-nueva')}
            onOpenLive={(id) => {
              setActiveClassroomId(id);
              setScreen('classroom-live');
            }}
            onClose={() => setScreen(homeScreen)}
          />
        )}

        {screen === 'classroom-live' && activeClassroomId && (
          <ClassroomLive
            classroomId={activeClassroomId}
            onBack={() => {
              setActiveClassroomId(null);
              setScreen('classrooms-list');
            }}
          />
        )}

        {screen === 'admin-audit' && <AuditView onClose={() => setScreen(homeScreen)} />}

        {screen === 'catalog' && (
          <OACatalogView siteId={historySiteId} onClose={() => setScreen(homeScreen)} />
        )}

        {screen === 'learner' && (
          <LearnerView
            siteId={historySiteId}
            onStartPilot={(learner) => {
              setPresetLearner(learner);
              setScreen(homeScreen);
            }}
            onOpenPilot={(sessionId) => {
              setScreen(homeScreen);
              void handleOpenFromHistory(sessionId);
            }}
            onClose={() => setScreen(homeScreen)}
          />
        )}

        {screen === 'report' && pilot && pilot.dictamen && (
          <ReportViewer
            dictamen={pilot.dictamen}
            chatHistory={pilot.chatHistory}
            studentName={pilot.input.student_name}
            oaCodigo={pilot.input.oa_codigo}
            hitoNombre={pilot.input.hito_nombre}
            sessionId={pilot.sessionId}
            siteId={pilot.siteId}
            onBackToList={handleBackToList}
          />
        )}
      </div>
    </AppShell>
  );
}

function formatError(e: unknown): string {
  if (e instanceof ApiError) {
    return `${e.code ?? 'ERROR'} — ${e.message}`;
  }
  if (e instanceof Error) return e.message;
  return String(e);
}

void (undefined as unknown as Modality | undefined);
